import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, LogOut, RefreshCw, Eye, Users, MousePointerClick, FileText, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_LABELS = {
  page_view: "Page visit",
  lang_switch: "Language switched",
  plan_choose: "Chose plan",
  whatsapp_click: "WhatsApp click",
  call_click: "Call click",
  form_submit: "Form submitted",
  banner_claim: "Banner offer click",
  hero_cta: "Hero button click",
  review_click: "Review button click",
  offer_claim: "Popup offer claim",
};

const fmtTime = (ts) => new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);

  const load = async () => {
    const [s, e] = await Promise.all([
      axios.get(`${API}/admin/stats`, { withCredentials: true }),
      axios.get(`${API}/contact-requests`, { withCredentials: true }),
    ]);
    setStats(s.data);
    setEnquiries(e.data);
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(async (r) => {
        setUser(r.data.user);
        await load();
      })
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const login = async (ev) => {
    ev.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(r.data.user);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    window.location.href = "/";
  };

  const removeEnquiry = async (id) => {
    await axios.delete(`${API}/contact-requests/${id}`, { withCredentials: true }).catch(() => {});
    setEnquiries((prev) => prev.filter((x) => x.id !== id));
  };

  const resetStats = async () => {
    if (!window.confirm("Reset all visitor statistics to zero? This cannot be undone.")) return;
    await axios.delete(`${API}/admin/analytics`, { withCredentials: true }).catch(() => {});
    await load();
  };

  return (
    <div className="min-h-screen bg-navy-900 text-cream">
      <header className="border-b border-brand/10 bg-navy-900/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <span className="font-serif font-semibold text-lg" data-testid="admin-header">Early Appointments — Admin</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cream/60 hover:text-brand transition-colors" data-testid="admin-back-link">
              <ArrowLeft size={14} /> Website
            </Link>
            {user && (
              <button onClick={logout} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cream/60 hover:text-brand transition-colors" data-testid="admin-logout-btn">
                <LogOut size={14} /> Logout
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {checking ? null : !user ? (
          <form onSubmit={login} className="max-w-sm mx-auto mt-16 border border-brand/20 bg-navy-800/40 p-8" data-testid="admin-login-form">
            <h1 className="font-serif font-semibold text-2xl mb-6">Admin Login</h1>
            <input
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-navy-900 border border-brand/25 px-4 py-3 text-sm mb-4 focus:outline-none focus:border-brand"
              data-testid="admin-email-input"
            />
            <input
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-navy-900 border border-brand/25 px-4 py-3 text-sm mb-4 focus:outline-none focus:border-brand"
              data-testid="admin-password-input"
            />
            {error && <p className="text-red-400 text-xs mb-4" data-testid="admin-login-error">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand text-white py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-brand-600 transition-colors disabled:opacity-50"
              data-testid="admin-login-submit-btn"
            >
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-serif font-semibold text-3xl">Visitor Dashboard</h1>
              <button onClick={load} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-brand-300 hover:text-brand transition-colors" data-testid="admin-refresh-btn">
                <RefreshCw size={14} /> Refresh
              </button>
              <button onClick={resetStats} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-cream/40 hover:text-red-400 transition-colors" data-testid="admin-reset-stats-btn">
                <Trash2 size={14} /> Reset Stats
              </button>
            </div>

            {stats && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                  {[
                    { icon: Eye, label: "Visits today", value: stats.today_views, tid: "stat-today" },
                    { icon: Users, label: "Visitors today", value: stats.today_visitors, tid: "stat-visitors-today" },
                    { icon: MousePointerClick, label: "Total visits", value: stats.page_views, tid: "stat-total" },
                    { icon: FileText, label: "Enquiries", value: enquiries.length, tid: "stat-enquiries" },
                  ].map((c) => (
                    <div key={c.tid} className="border border-brand/20 bg-navy-800/40 p-5" data-testid={c.tid}>
                      <c.icon size={16} className="text-brand-300 mb-3" />
                      <p className="font-serif font-semibold text-3xl">{c.value}</p>
                      <p className="text-xs text-cream/50 mt-1 uppercase tracking-wider">{c.label}</p>
                    </div>
                  ))}
                </div>

                <div className="grid lg:grid-cols-3 gap-6 mb-10">
                  <div className="border border-brand/20 bg-navy-800/40 p-6" data-testid="admin-countries">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-5">Where visitors are from</h2>
                    {stats.countries.length === 0 && <p className="text-sm text-cream/40">No data yet — visits will appear here.</p>}
                    {stats.countries.map((c) => (
                      <div key={c.country} className="flex items-center gap-3 mb-3">
                        <span className="text-sm w-32 truncate">{c.country}</span>
                        <div className="flex-1 h-1.5 bg-navy-900">
                          <div className="h-full bg-brand" style={{ width: `${Math.round((c.count / stats.countries[0].count) * 100)}%` }} />
                        </div>
                        <span className="text-xs text-cream/50 w-8 text-right">{c.count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border border-brand/20 bg-navy-800/40 p-6" data-testid="admin-sources">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-5">How visitors found you</h2>
                    {(!stats.sources || stats.sources.length === 0) && <p className="text-sm text-cream/40">No data yet.</p>}
                    {(stats.sources || []).map((s) => (
                      <div key={s.source} className="flex items-center gap-3 mb-3">
                        <span className="text-sm w-32 truncate">{s.source}</span>
                        <div className="flex-1 h-1.5 bg-navy-900">
                          <div className="h-full bg-brand-300" style={{ width: `${Math.round((s.count / stats.sources[0].count) * 100)}%` }} />
                        </div>
                        <span className="text-xs text-cream/50 w-8 text-right">{s.count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border border-brand/20 bg-navy-800/40 p-6" data-testid="admin-actions">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-5">What visitors clicked</h2>
                    {stats.actions.length === 0 && <p className="text-sm text-cream/40">No clicks tracked yet.</p>}
                    {stats.actions.map((a) => (
                      <div key={a.event} className="flex items-center justify-between mb-3">
                        <span className="text-sm">{EVENT_LABELS[a.event] || a.event}{a.event === "page_view" ? "" : ""}</span>
                        <span className="text-xs text-brand-300 font-bold">{a.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-brand/20 bg-navy-800/40 p-6 mb-10" data-testid="admin-activity">
                  <h2 className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-5">Recent activity</h2>
                  {stats.recent.length === 0 && <p className="text-sm text-cream/40">No activity yet.</p>}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {stats.recent.map((r) => (
                      <div key={r.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm border-b border-brand/5 pb-2">
                        <span className="text-cream/40 text-xs w-28 flex-none">{fmtTime(r.ts)}</span>
                        <span className="text-brand-300">{EVENT_LABELS[r.event] || r.event}</span>
                        {r.meta && <span className="text-cream/70 text-xs">{r.meta}</span>}
                        <span className="text-cream/40 text-xs ml-auto">{[r.city, r.country].filter(Boolean).join(", ")}{r.source ? ` · via ${r.source}` : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="border border-brand/20 bg-navy-800/40 p-6" data-testid="admin-enquiries">
              <h2 className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-5">Enquiries ({enquiries.length})</h2>
              {enquiries.length === 0 && <p className="text-sm text-cream/40">No enquiries yet.</p>}
              <div className="space-y-3">
                {enquiries.map((e) => (
                  <div key={e.id} className="border border-brand/10 bg-navy-900/60 p-4 text-sm">
                    <div className="flex flex-wrap justify-between gap-2">
                      <span className="font-semibold">{e.full_name}</span>
                      <span className="flex items-center gap-2">
                        {e.source && (
                          <span className="text-[10px] text-cream/50 border border-brand/25 px-2 py-0.5 uppercase tracking-wider" data-testid="enquiry-source">
                            via {e.source}
                          </span>
                        )}
                        <span className="text-xs text-brand-300 uppercase tracking-wider">{e.status}</span>
                        <button
                          onClick={() => removeEnquiry(e.id)}
                          aria-label="Delete enquiry"
                          data-testid={`enquiry-delete-btn-${e.id}`}
                          className="text-cream/30 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </span>
                    </div>
                    <p className="text-cream/50 text-xs mt-1">{e.email} · {e.country_code} {e.phone} · {e.visa_category}</p>
                    <p className="text-cream/50 text-xs mt-1">{e.locations}{e.message ? ` — ${e.message}` : ""}</p>
                    <p className="text-cream/30 text-[10px] mt-2">{fmtTime(e.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
